import React, { useState, useEffect } from "react";
import { X, Loader, Plus } from "lucide-react";
import SearchableSelect from "./SearchableSelect";
import { masterService } from "../api/masterService";
import { useNotification } from "../contexts/NotificationContext";
import {
  formatNumberWithSeparator,
  parseNumberFromSeparator,
} from "../utils/numberFormat";

export default function CompositionFormModal({
  isOpen,
  onClose,
  type,
  onSave,
  initialData,
  serviceId,
}) {
  const { showSuccess, showError } = useNotification();
  const [selectedItem, setSelectedItem] = useState("");
  const [serviceName, setServiceName] = useState("");
  const [usedQty, setUsedQty] = useState("");
  const [unitId, setUnitId] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const isProduct = type === "product";
  const isEdit = !!initialData;
  const title = isEdit
    ? isProduct
      ? "Edit Komposisi Produk"
      : "Edit Komposisi Jasa"
    : isProduct
      ? "Tambah Komposisi Produk"
      : "Tambah Komposisi Jasa";

  const subtitle = isProduct
    ? "Bahan/produk yang akan otomatis dipotong dari stok saat layanan terjual"
    : "Komponen jasa atau biaya operasional yang tidak mengurangi stok fisik";

  const handleAddUnit = async (uName) => {
    try {
      const unitCode = uName.toUpperCase().replace(/\s+/g, "_");
      const res = await masterService.units.create({ name: uName, code: unitCode });
      const newUnit = res.data?.data || res.data;
      if (newUnit && newUnit.id) {
        setUnits((prev) => [newUnit, ...prev]);
        setUnitId(newUnit.id);
        showSuccess(`Satuan "${newUnit.name}" berhasil ditambahkan`);
        return newUnit;
      }
    } catch (err) {
      showError(err.message || "Gagal menambah satuan");
      throw err;
    }
  };

  // Fetch items and units when modal opens
  useEffect(() => {
    if (!isOpen || !type) return;

    const fetchData = async () => {
      try {
        setLoading(true);

        const promises = [masterService.units.listAll({ status: "ACTIVE" })];

        if (isProduct) {
          promises.unshift(masterService.products.listAll({ status: "ACTIVE" }));
        }

        const responses = await Promise.all(promises);

        if (isProduct) {
          const productList = Array.isArray(responses[0]) ? responses[0] : (responses[0]?.data || responses[0]?.items || []);
          const unitList = Array.isArray(responses[1]) ? responses[1] : (responses[1]?.data || responses[1]?.items || []);
          setItems(productList);
          setUnits(unitList);
        } else {
          const unitList = Array.isArray(responses[0]) ? responses[0] : (responses[0]?.data || responses[0]?.items || []);
          setItems([]);
          setUnits(unitList);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        showError(err.message || "Gagal memuat data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, type, isProduct, serviceId]);

  // Load initial data for edit mode
  useEffect(() => {
    if (initialData) {
      setSelectedItem(initialData.product_id || initialData.service_id || "");
      setServiceName(initialData.service_name || initialData.name || "");
      setUsedQty(initialData.quantity || initialData.used ? String(initialData.quantity || initialData.used) : "");
      setUnitId(initialData.unit_id || "");
      setUnitPrice(initialData.unit_price || initialData.sell_price ? String(initialData.unit_price || initialData.sell_price) : "");
    } else {
      setSelectedItem("");
      setServiceName("");
      setUsedQty("");
      setUnitId("");
      setUnitPrice("");
    }
  }, [initialData, isOpen]);

  const handleSubmit = async () => {
    if (isProduct) {
      if (!selectedItem) {
        showError("Produk harus dipilih");
        return;
      }
    } else {
      if (!serviceName.trim()) {
        showError("Nama jasa harus diisi");
        return;
      }
    }

    if (!usedQty) {
      showError("Jumlah digunakan harus diisi");
      return;
    }

    if (!unitId) {
      showError("Satuan harus dipilih");
      return;
    }

    if (!unitPrice) {
      showError("Harga satuan harus diisi");
      return;
    }

    const used = parseNumberFromSeparator(usedQty);
    const price = parseNumberFromSeparator(unitPrice);

    if (isNaN(used) || used <= 0) {
      showError("Jumlah digunakan harus berupa angka positif");
      return;
    }

    if (isNaN(price) || price < 0) {
      showError("Harga satuan harus berupa angka valid");
      return;
    }

    try {
      setSaving(true);

      const payload = isProduct
        ? {
            product_id: selectedItem,
            unit_id: unitId,
            used: used,
            sell_price: price,
          }
        : {
            name: serviceName.trim(),
            unit_id: unitId,
            used: used,
            sell_price: price,
          };

      await onSave(payload);
      showSuccess("Komposisi berhasil disimpan");
      onClose();
    } catch (err) {
      console.error("Error saving composition:", err);
      showError(err.message || "Gagal menyimpan komposisi");
    } finally {
      setSaving(false);
    }
  };

  const handleItemChange = (itemId) => {
    setSelectedItem(itemId);

    // Auto-fill price and unit from selected item
    const selectedItemData = items.find((item) => item.id === itemId);
    if (selectedItemData) {
      const price = selectedItemData.sell_price ?? selectedItemData.price ?? 0;
      if (price > 0) {
        setUnitPrice(String(price));
      }
      if (selectedItemData.unit_id) {
        setUnitId(selectedItemData.unit_id);
      }
    }
  };

  const calculateTotal = () => {
    const used = parseNumberFromSeparator(usedQty);
    const price = parseNumberFromSeparator(unitPrice);
    if (isNaN(used) || isNaN(price)) return 0;
    return used * price;
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(val || 0);
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={() => {
        if (!saving) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl ring-1 ring-slate-200/70 w-full max-w-lg flex flex-col max-h-[90vh] shadow-2xl overflow-hidden modal-panel-enter">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-teal-600 text-white rounded-t-2xl">
          <div>
            <h2 className="text-base font-semibold tracking-tight">{title}</h2>
            <p className="text-xs text-teal-100 mt-0.5">{subtitle}</p>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              {isProduct ? "Produk Komposisi" : "Nama Jasa / Komponen"}{" "}
              <span className="text-rose-500">*</span>
            </label>
            {isProduct ? (
              <>
                <SearchableSelect
                  options={items}
                  value={selectedItem}
                  onChange={handleItemChange}
                  fetchOptions={async (term) => {
                    const response = await masterService.products.list({
                      status: "ACTIVE",
                      query: term,
                      limit: 100,
                    });
                    return response.data || response.items || (Array.isArray(response) ? response : []);
                  }}
                  minSearchLength={1}
                  placeholder={loading ? "Memuat produk..." : "Pilih produk komposisi..."}
                  disabled={loading || saving || isEdit}
                  displayKey="name"
                  valueKey="id"
                />
                {loading && (
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                    <Loader className="w-3 h-3 animate-spin" />
                    <span>Memuat data produk...</span>
                  </div>
                )}
              </>
            ) : (
              <input
                type="text"
                value={serviceName}
                onChange={(e) => setServiceName(e.target.value)}
                disabled={saving}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors disabled:bg-slate-100"
                placeholder="Contoh: Jasa Pengerjaan / Fee Mekanik / Biaya Styling"
              />
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Harga Satuan / Tarif <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm font-medium">
                Rp
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={formatNumberWithSeparator(unitPrice)}
                onChange={(e) =>
                  setUnitPrice(e.target.value.replace(/\./g, ""))
                }
                disabled={saving}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors disabled:bg-slate-100 font-medium"
                placeholder="0"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Jumlah Digunakan <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={formatNumberWithSeparator(usedQty)}
                onChange={(e) => setUsedQty(e.target.value.replace(/\./g, ""))}
                disabled={saving}
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors disabled:bg-slate-100 font-medium"
                placeholder="Contoh: 1"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Satuan <span className="text-rose-500">*</span>
              </label>
              <SearchableSelect
                options={units}
                value={unitId}
                onChange={(value) => setUnitId(value)}
                onAddNew={handleAddUnit}
                addNewLabel="Tambah Satuan Baru"
                fetchOptions={async (term) => {
                  const response = await masterService.units.list({
                    status: "ACTIVE",
                    query: term,
                  });
                  return response.data || [];
                }}
                minSearchLength={1}
                placeholder={loading ? "Memuat satuan..." : "Pilih satuan..."}
                disabled={loading || saving}
                displayKey="name"
                valueKey="id"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Total Biaya Komposisi
            </label>
            <input
              type="text"
              value={formatCurrency(calculateTotal())}
              disabled
              className="w-full bg-teal-50 ring-1 ring-teal-200 border-0 rounded-lg px-3 py-2 text-sm text-teal-900 font-bold cursor-not-allowed"
              placeholder="Rp 0"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/40 flex justify-end gap-3 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2 bg-white ring-1 ring-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm transition-colors disabled:opacity-50">
            Batal
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || saving}
            className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium text-sm transition-colors shadow-sm shadow-teal-600/20 disabled:opacity-50 flex items-center gap-2">
            {saving && <Loader className="w-4 h-4 animate-spin" />}
            {saving ? "Menyimpan..." : "Simpan Data"}
          </button>
        </div>
      </div>
    </div>
  );
}
